import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const THEME_STORAGE_KEY = 'mrt.ui.theme.v1';

function safeParseJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function loadThemeState() {
  const raw = typeof window !== 'undefined' ? window.localStorage.getItem(THEME_STORAGE_KEY) : null;
  const parsed = raw ? safeParseJson(raw) : null;
  const globalTheme = parsed?.globalTheme === 'dark' ? 'dark' : 'light';
  const receptionCheckOverride =
    parsed?.overrides?.receptionCheck === 'dark'
      ? 'dark'
      : parsed?.overrides?.receptionCheck === 'light'
        ? 'light'
        : null;
  const scheduleOverride =
    parsed?.overrides?.schedule === 'dark'
      ? 'dark'
      : parsed?.overrides?.schedule === 'light'
        ? 'light'
        : null;

  return {
    globalTheme,
    overrides: { receptionCheck: receptionCheckOverride, schedule: scheduleOverride },
  };
}

function saveThemeState(state) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    THEME_STORAGE_KEY,
    JSON.stringify({
      globalTheme: state.globalTheme,
      overrides: {
        receptionCheck: state.overrides?.receptionCheck ?? null,
        schedule: state.overrides?.schedule ?? null,
      },
    }),
  );
  try {
    window.dispatchEvent(new Event('mrt-theme-storage'));
  } catch {
    /* ignore */
  }
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const location = useLocation();
  const [state, setState] = useState(() => loadThemeState());
  const skipSaveRef = useRef(false);

  useEffect(() => {
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }
    saveThemeState(state);
  }, [state]);

  useEffect(() => {
    const syncFromStorage = () => {
      skipSaveRef.current = true;
      setState(loadThemeState());
    };
    window.addEventListener('mrt-theme-storage', syncFromStorage);
    const onStorage = (e) => {
      if (e.key === THEME_STORAGE_KEY) syncFromStorage();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('mrt-theme-storage', syncFromStorage);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const effectiveTheme = useMemo(() => {
    const pathname = String(location?.pathname || '/');
    if (pathname === '/reception-check') {
      return state.overrides?.receptionCheck || state.globalTheme;
    }
    if (pathname === '/schedule' || pathname.startsWith('/schedule/')) {
      return state.overrides?.schedule || state.globalTheme;
    }
    return state.globalTheme;
  }, [location?.pathname, state.globalTheme, state.overrides]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', effectiveTheme);
  }, [effectiveTheme]);

  const api = useMemo(() => {
    return {
      globalTheme: state.globalTheme,
      receptionCheckTheme: state.overrides?.receptionCheck,
      scheduleTheme: state.overrides?.schedule,
      effectiveTheme,
      setGlobalTheme: (t) =>
        setState((prev) => ({
          ...prev,
          globalTheme: t === 'dark' ? 'dark' : 'light',
        })),
      setReceptionCheckTheme: (tOrNull) =>
        setState((prev) => ({
          ...prev,
          overrides: {
            ...(prev.overrides || {}),
            receptionCheck: tOrNull === 'dark' ? 'dark' : tOrNull === 'light' ? 'light' : null,
          },
        })),
      setScheduleTheme: (tOrNull) =>
        setState((prev) => ({
          ...prev,
          overrides: {
            ...(prev.overrides || {}),
            receptionCheck: prev.overrides?.receptionCheck ?? null,
            schedule: tOrNull === 'dark' ? 'dark' : tOrNull === 'light' ? 'light' : null,
          },
        })),
      resetThemeSettings: () => setState({ globalTheme: 'light', overrides: { receptionCheck: null, schedule: null } }),
    };
  }, [effectiveTheme, state.globalTheme, state.overrides]);

  return <ThemeContext.Provider value={api}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
